import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FilmSlate, Download, Copy, ArrowClockwise, PaperPlaneTilt } from "@phosphor-icons/react";
import client from "../../lib/api";

const videoSrc = (r) => r.cdn_url || `${process.env.REACT_APP_BACKEND_URL}${r.mp4_url}`;

export const pollJob = async (jobId, timeoutMs = 300000) => {
  const t0 = Date.now();
  await new Promise((r) => setTimeout(r, 1000));
  while (Date.now() - t0 < timeoutMs) {
    const { data } = await client.get(`/admin/dnp/tiktok-video/job/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") throw new Error(data.error || "Render failed");
    await new Promise((r) => setTimeout(r, 3000));
  }
  throw new Error("Render timed out");
};

/* Content Studio card — 15s Win Wall reel, render now + Friday auto-email. */
export function WinWallReelCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const load = () => client.get("/admin/win-wall-reel").then((r) => setSt(r.data)).catch(() => setSt({ latest: null, wall_wins: 0, schedule: {} }));
  useEffect(() => { load(); }, []);

  const render = async (force) => {
    setBusy(true);
    try {
      const { data } = await client.post(`/admin/win-wall-reel/render?force=${force ? "true" : "false"}`);
      const result = await pollJob(data.job_id);
      setSt((s) => ({ ...s, latest: result }));
      toast.success(result.cached ? "Reel is up to date (cached)" : `Reel rendered — ${result.wins} first loads, ${result.duration_sec}s`);
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Render failed"); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await client.post("/admin/win-wall-reel/send-weekly-now");
      toast.success(data.status === "sent" ? "Friday email sent to you" : data.status === "skipped" ? `Skipped — ${data.reason}` : "Rendered (email didn't go through)");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't send"); }
    finally { setSending(false); }
  };
  const copy = async (text, label) => { try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.error("Couldn't copy"); } };

  if (!st) return null;
  const r = st.latest;
  return (
    <div className="card-surface p-5 sm:p-6 border border-emerald-500/20" data-testid="win-wall-reel-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 font-bold"><FilmSlate size={14} weight="fill" className="text-emerald-400" /> Win Wall reel · ~15s · AI voiceover</div>
          <div className="text-sm text-neutral-300 mt-1">Newest first-load cards stitched into a vertical TikTok with an onyx voiceover (rates hidden). <b className="text-white" data-testid="win-wall-reel-count">{st.wall_wins}</b> card{st.wall_wins === 1 ? "" : "s"} on the wall. <span className="text-neutral-500">{st.schedule?.label}</span></div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => render(!!r)} disabled={busy || !st.wall_wins} className="btn-primary rounded-full px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="win-wall-reel-render">
            <ArrowClockwise size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Rendering…" : r ? "Re-render" : "Render reel"}
          </button>
          <button type="button" onClick={sendNow} disabled={sending || !st.wall_wins} className="rounded-full border border-white/15 hover:border-emerald-400/60 px-4 py-2 text-xs font-bold text-neutral-200 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="win-wall-reel-send-now">
            <PaperPlaneTilt size={14} /> {sending ? "Sending…" : "Email me this week's"}
          </button>
        </div>
      </div>
      {!st.wall_wins && <div className="text-sm text-neutral-500" data-testid="win-wall-reel-empty">No wins on the wall yet — the reel unlocks when the first carrier posts a first-load win.</div>}
      {r && (
        <div className="flex flex-col md:flex-row gap-5 mt-2" data-testid="win-wall-reel-latest">
          <video src={videoSrc(r)} controls playsInline className="w-40 rounded-xl border border-white/10 bg-black aspect-[9/16]" data-testid="win-wall-reel-video" />
          <div className="flex-1 min-w-0 text-sm space-y-3">
            <div className="text-neutral-400 text-xs">{r.wins} first loads · {r.duration_sec}s · <span data-testid="win-wall-reel-voice">{r.voiceover ? `voiceover (${r.voice})` : "silent — add a trending sound"}</span> · rendered {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{r.cdn_url ? " · CDN" : ""}</div>
            <div><div className="text-[11px] uppercase tracking-widest text-neutral-500 font-bold mb-1">TikTok caption ({r.captions.tiktok.length}/150)</div><p className="text-neutral-200 text-xs" data-testid="win-wall-reel-caption">{r.captions.tiktok}</p></div>
            <div className="flex flex-wrap gap-2">
              <a href={videoSrc(r)} download="win-wall-reel.mp4" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-bold text-neutral-200 hover:bg-white/[0.06]" data-testid="win-wall-reel-download"><Download size={12} /> Download MP4</a>
              <button type="button" onClick={() => copy(r.captions.tiktok, "TikTok caption")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-bold text-neutral-200 hover:bg-white/[0.06]" data-testid="win-wall-reel-copy-tiktok"><Copy size={12} /> Copy TikTok caption</button>
              <button type="button" onClick={() => copy(r.captions.facebook, "Facebook post")} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-bold text-neutral-200 hover:bg-white/[0.06]" data-testid="win-wall-reel-copy-facebook"><Copy size={12} /> Copy FB post</button>
            </div>
            {st.last_weekly && <div className="text-[11px] text-neutral-500" data-testid="win-wall-reel-last-weekly">Last Friday email: week {st.last_weekly.week} · {st.last_weekly.sent ? "delivered" : "not delivered"}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
